import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from "@angular/core";
import { combineLatest, ReplaySubject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UiQuery } from "../../configuration/state/ui.query";
import { NumberGridConfiguration } from "../../configuration/state/ui.store";

@Component({
	selector: "app-number-grid",
	templateUrl: "./number-grid.component.html",
	styleUrls: ["./number-grid.component.scss"],
})
export class NumberGridComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	private static readonly DEFAULT_COLUMNS = 5;

	constructor(
		private uiQuery: UiQuery,
		private changeDetectorRef: ChangeDetectorRef,
	) {}

	@Input() selectedValue: number;
	@Output() selectedValueChange = new EventEmitter<number>();

	// Optional upper bound. When provided (not null/undefined), the grid is built directly as
	// 0..maxValue (ascending) and the device-derived amount (numberGridConfiguration/cartsInUse/
	// seatsUnavailable) is ignored. When absent, behaviour is EXACTLY as before, so the legacy
	// riders widget (which passes no maxValue) is unaffected.
	@Input() maxValue?: number;

	// When the grid is driven by maxValue, controls the ordering of the built 0..maxValue range.
	// True renders it descending (maxValue first, 0 last). Ignored on the legacy device-driven path,
	// which keeps using its own numberGridConfiguration.direction.
	@Input() descending?: boolean;

	// Optional visible column count for the grid. Defaults to 5, which is the legacy layout the
	// riders widget relies on, so callers that omit it are unaffected. The asset-dispatch widget
	// opts into 4 to make the numpad (and therefore the centre card) narrower. This only changes the
	// visible width/wrap: the full 0..maxValue range still renders and scrolls when it overflows.
	@Input() columns?: number;

	@Input() variant: "default" | "v2" = "default";

	@ViewChild("grid", { static: false }) grid?: ElementRef<HTMLElement>;

	numbers: number[];
	showScrollFade = false;

	private destroyed$: ReplaySubject<void> = new ReplaySubject(1);
	private resizeObserver?: ResizeObserver;
	private scrollFadeCheckPending = false;

	get columnCount(): number {
		return this.columns != null && this.columns > 0 ? this.columns : NumberGridComponent.DEFAULT_COLUMNS;
	}

	ngOnInit() {
		const configurationObservable = combineLatest([
			this.uiQuery.numberGridConfiguration$,
			this.uiQuery.cartsInUse$,
			this.uiQuery.seatsUnavailable$,
		]);

		configurationObservable.pipe(takeUntil(this.destroyed$)).subscribe((values) => {
			// When a maxValue is supplied the grid is driven by it, so ignore device-derived updates.
			if (this.maxValue == null) {
				this.configure(...values);
			}
		});
	}

	ngAfterViewInit() {
		if (typeof ResizeObserver === "undefined") {
			return;
		}

		this.resizeObserver = new ResizeObserver(() => this.updateScrollFade());
		this.observeGrid();
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Rebuild from maxValue whenever it (or the ordering) changes (e.g. available seats shrink/grow,
		// or the operator toggles occupied/empty counting) so the grid always matches the current cap
		// and direction. A no-op when maxValue is not provided.
		if (("maxValue" in changes || "descending" in changes || "columns" in changes) && this.maxValue != null) {
			this.configureFromMaxValue(this.maxValue);
		}

		// When maxValue shrinks below the current selection (e.g. more seats became unavailable),
		// clamp the bound value down AND emit so the parent's two-way `selectedValue` follows. Only
		// applies on the maxValue-driven path; the legacy device-driven path (no maxValue) is
		// untouched. Uses select() so the same clamp+emit logic as a tap is reused.
		if (this.maxValue != null && this.selectedValue != null && this.selectedValue > this.maxValue) {
			this.select(this.maxValue);
		}

		if ("variant" in changes || "columns" in changes) {
			queueMicrotask(() => this.observeGrid());
		}
	}

	select(value: number) {
		if (value == null) {
			return;
		}

		this.selectedValue = value;
		this.selectedValueChange.emit(value);
	}

	private configure(configuration: NumberGridConfiguration, cartsInUse: number, seatsUnavailable: number) {
		let amount = configuration.amount;

		if (cartsInUse == 1) {
			amount -= seatsUnavailable;
		}

		if (amount <= 0) {
			amount = 0;
		}

		// Add one to allow making empty dispatches.
		amount++;

		const numbers = Array.from({ length: amount }, (_, index) => index);
		if (configuration.direction === "descending") {
			numbers.reverse();
		}

		this.numbers = numbers;
		this.populateEmptyColumns();
		queueMicrotask(() => this.observeGrid());
	}

	// Builds the grid as 0..maxValue inclusive. 0 stays selectable so empty dispatches remain
	// possible. Reverses to maxValue..0 when `descending` is set. Reuses the same empty-column fill
	// as the device-driven path.
	private configureFromMaxValue(maxValue: number) {
		const amount = Math.max(0, maxValue) + 1;
		const numbers = Array.from({ length: amount }, (_, index) => index);
		if (this.descending) {
			numbers.reverse();
		}

		this.numbers = numbers;
		this.populateEmptyColumns();
		queueMicrotask(() => this.observeGrid());
	}

	private populateEmptyColumns() {
		if (this.variant === "v2") {
			return;
		}

		// Fill missing columns with empty fields.
		const columnsPerRow = this.columnCount;
		const columnsUsed = this.numbers.length % columnsPerRow || columnsPerRow;

		const emptyColumns = columnsPerRow - columnsUsed;
		if (emptyColumns > 0) {
			this.numbers = this.numbers.concat([...Array(emptyColumns)]);
		}
	}

	private observeGrid() {
		const el = this.grid?.nativeElement;
		if (!el || !this.resizeObserver) {
			this.updateScrollFade();
			return;
		}

		this.resizeObserver.disconnect();
		this.resizeObserver.observe(el);
		this.updateScrollFade();
	}

	private updateScrollFade() {
		if (this.scrollFadeCheckPending) {
			return;
		}

		this.scrollFadeCheckPending = true;
		queueMicrotask(() => {
			this.scrollFadeCheckPending = false;

			const el = this.grid?.nativeElement;
			const next = this.variant === "v2" && !!el && el.scrollHeight > el.clientHeight + 1;
			if (next === this.showScrollFade) {
				return;
			}

			this.showScrollFade = next;
			this.changeDetectorRef.markForCheck();
		});
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
		this.destroyed$.next();
		this.destroyed$.complete();
	}
}
